import { useEffect, useRef } from "react";
import { useLocalStorage } from "react-use";

/**
 * Tracks whether the user has viewed the welcome message for a given game.
 *
 * This uses local storage to persist the viewed state across sessions.
 *
 * If `recordView` is true, it will mark the message as viewed when the hook is
 * used.
 *
 * Otherwise, it will only read the viewed state without updating it.
 */
export function useMessageViewTracking(
  gameId: string,
  {
    recordView = false,
  }: {
    recordView?: boolean;
  } = {},
) {
  const [hasViewed, setHasViewed] = useLocalStorage(
    getLocalStorageKey(gameId),
    false,
  );

  const hadViewedRef = useRef(!!hasViewed);

  useEffect(() => {
    if (recordView && !hasViewed) {
      setHasViewed(true);
    }
  }, [hasViewed, setHasViewed, recordView]);

  return {
    hasAlreadyViewed: hadViewedRef.current,
  };
}

function getLocalStorageKey(gameId: string) {
  return `games::${gameId}::welcome-message-viewed`;
}
